/**
 * Accessibility Provider Component
 * 
 * This component provides accessibility context and features to the application.
 * It follows the ConnectPath Modular Methodology, particularly Module M15 (Error Handling).
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

interface AccessibilityContextType {
  highContrast: boolean;
  toggleHighContrast: () => void;
  fontSize: 'normal' | 'large' | 'x-large';
  setFontSize: (size: 'normal' | 'large' | 'x-large') => void;
  announceMessage: (message: string) => void;
}

const defaultContext: AccessibilityContextType = {
  highContrast: false,
  toggleHighContrast: () => {},
  fontSize: 'normal',
  setFontSize: () => {},
  announceMessage: () => {}
};

const AccessibilityContext = createContext<AccessibilityContextType>(defaultContext);

export const useAccessibility = () => useContext(AccessibilityContext);

interface AccessibilityProviderProps {
  children: React.ReactNode;
}

export const AccessibilityProvider: React.FC<AccessibilityProviderProps> = ({ children }) => {
  // Use React state only for now (temporary fix until proper persistence is implemented)
  const [highContrast, setHighContrast] = useState<boolean>(false);
  const [fontSize, setFontSize] = useState<'normal' | 'large' | 'x-large'>('normal');
  const [announcement, setAnnouncement] = useState<string>('');

  // Preferences are no longer persisted to localStorage
  // This is a temporary fix until a proper solution is implemented

  // Apply high contrast mode
  useEffect(() => {
    if (highContrast) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }
  }, [highContrast]);

  // Apply font size
  useEffect(() => {
    document.body.classList.remove('font-size-normal', 'font-size-large', 'font-size-x-large');
    document.body.classList.add(`font-size-${fontSize}`);
  }, [fontSize]);

  // Handle screen reader announcements
  const announceMessage = (message: string) => {
    setAnnouncement(message);
  };

  // Clear announcement after it's been read
  useEffect(() => {
    if (announcement) {
      const timer = setTimeout(() => {
        setAnnouncement('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [announcement]);

  const toggleHighContrast = () => {
    setHighContrast(prev => !prev);
  };

  const handleSetFontSize = (size: 'normal' | 'large' | 'x-large') => {
    setFontSize(size);
  };

  const value = {
    highContrast,
    toggleHighContrast,
    fontSize,
    setFontSize: handleSetFontSize,
    announceMessage
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
      {/* Screen reader announcement area */}
      <div 
        aria-live="assertive" 
        aria-atomic="true" 
        className="sr-only"
        style={{ 
          position: 'absolute', 
          width: '1px', 
          height: '1px', 
          padding: 0, 
          margin: '-1px', 
          overflow: 'hidden', 
          clip: 'rect(0, 0, 0, 0)', 
          whiteSpace: 'nowrap', 
          border: 0 
        }}
      >
        {announcement}
      </div>
    </AccessibilityContext.Provider>
  );
};